import {
  BID_SOUNDS,
  DEFAULT_BID_SOUND,
  bidSoundsStartMuted,
  type BidSoundId,
} from '@auctisync/api-client';

const MUTED_KEY = 'auctisync:bidSoundMuted';
const PRESET_KEY = 'auctisync:bidSoundPreset';

/**
 * The sounds on offer are the SHARED list (`BID_SOUNDS`, @auctisync/api-client)
 * since 2026-10-11, when the app began to play them: one set of ids and names
 * for both surfaces. This page serves each as `/sounds/<id>.m4a`.
 */
export type SoundPresetId = BidSoundId;

export interface SoundPreset {
  id: SoundPresetId;
  label: string;
  file: string;
}

export const SOUND_PRESETS: readonly SoundPreset[] = BID_SOUNDS.map((s) => ({
  id: s.id,
  label: s.label,
  file: `/sounds/${s.id}.m4a`,
}));

const DEFAULT_PRESET: SoundPresetId = DEFAULT_BID_SOUND;
const GAVEL_FILE = '/sounds/gavel.m4a';

/**
 * Plays AuctiSync's real audio cues (curated MP3 / M4A files served from
 * `apps/web/public/sounds/`). Each preset is an HTMLAudioElement created
 * lazily and reused for the life of the page; resetting `currentTime`
 * before every play lets rapid repeat events fire without waiting for the
 * clip to finish.
 *
 * Browsers still block audio playback until the first user gesture. We
 * warm the element on page load (preload=auto triggers a fetch) and call
 * `.play()` defensively — the promise rejects silently when autoplay is
 * blocked, which is exactly what we want.
 */

const audioCache = new Map<string, HTMLAudioElement>();

function getAudio(src: string): HTMLAudioElement | null {
  if (typeof window === 'undefined') return null;
  let el = audioCache.get(src);
  if (el) return el;
  try {
    el = new Audio(src);
    el.preload = 'auto';
    el.crossOrigin = 'anonymous';
    audioCache.set(src, el);
    return el;
  } catch {
    return null;
  }
}

// Preload every preset + the gavel on first module load so the first play
// doesn't stall on a network fetch.
if (typeof window !== 'undefined') {
  for (const p of SOUND_PRESETS) getAudio(p.file);
  getAudio(GAVEL_FILE);
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function isBidSoundMuted(): boolean {
  if (typeof localStorage === 'undefined') return true;
  const stored = localStorage.getItem(MUTED_KEY);
  if (stored === null) {
    return bidSoundsStartMuted(prefersReducedMotion());
  }
  return stored === '1';
}

export function setBidSoundMuted(muted: boolean): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(MUTED_KEY, muted ? '1' : '0');
}

export function getBidSoundPreset(): SoundPresetId {
  if (typeof localStorage === 'undefined') return DEFAULT_PRESET;
  const stored = localStorage.getItem(PRESET_KEY);
  if (stored && SOUND_PRESETS.some((p) => p.id === stored)) {
    return stored as SoundPresetId;
  }
  return DEFAULT_PRESET;
}

export function setBidSoundPreset(id: SoundPresetId): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(PRESET_KEY, id);
}

function play(src: string): void {
  const el = getAudio(src);
  if (!el) return;
  try {
    el.currentTime = 0;
  } catch {
    // Some browsers throw if the element isn't loaded yet. Swallow.
  }
  const playPromise: Promise<void> | undefined = el.play();
  if (playPromise !== undefined && typeof playPromise.catch === 'function') {
    // Autoplay blocked or other playback failure. Nothing we can do
    // without a user gesture; the next successful gesture will unlock.
    void playPromise.catch(() => {});
  }
}

/**
 * Play the currently-selected bid sound (or a specific preset if passed).
 * `bypassMute` is used by the preview button in the sound picker so the
 * user can hear what they're choosing even while muted.
 */
export function playBidSound(options?: { preset?: SoundPresetId; bypassMute?: boolean }): void {
  if (!options?.bypassMute && isBidSoundMuted()) return;
  const preset = options?.preset ?? getBidSoundPreset();
  const entry = SOUND_PRESETS.find((p) => p.id === preset);
  play(entry?.file ?? '/sounds/happy-beeps.m4a');
}

/**
 * Play the gavel strike — used on lot-close transitions (active → sold /
 * unsold / no_sale). Respects the same mute toggle as bid sounds.
 */
export function playGavelSound(options?: { bypassMute?: boolean }): void {
  if (!options?.bypassMute && isBidSoundMuted()) return;
  play(GAVEL_FILE);
}

/**
 * Play what the shared sound rule decided (`lotSoundFor`, `@auctisync/api-client`):
 * the chosen bid sound, the gavel, or nothing. The rule is shared with the
 * app; only the playing is the website's. Respects the mute.
 */
export function playLotSound(sound: 'bid' | 'gavel' | null): void {
  if (sound === 'gavel') playGavelSound();
  else if (sound === 'bid') playBidSound();
}
